/* Thermal printing of tickets: the slip reception hands a patient with their
 * serial number on it.
 *
 * Two ways to reach the printer, chosen per device in App settings:
 *  - "system": the device's own print dialog, laid out for a 58 or 80 mm roll.
 *    Works from Chrome on a desk computer or a phone with the printer's driver
 *    or print service installed.
 *  - "rawbt": the RawBT app on Android (free on the Play Store), which drives
 *    almost any Bluetooth or USB thermal printer. The slip goes as ESC/POS
 *    bytes. This is the way from the Android app, whose web view has no print
 *    dialog of its own.
 *
 * Slips are printed in English: most thermal printers have no Indic fonts in
 * text mode, the same reason printed receipts stay in English.
 */

export type PrintMethod = 'system' | 'rawbt'
export type PaperWidth = 58 | 80

export interface PrinterPrefs {
  method: PrintMethod
  paper: PaperWidth
  /** Print the ticket by itself as soon as a booking is made. */
  autoPrint: boolean
}

export interface TicketSlip {
  clinicName: string
  clinicAddress?: string | null
  clinicPhone?: string | null
  /** "OPD TOKEN", "LAB TOKEN". */
  heading: string
  serial?: number | null
  /** The doctor, or the test. */
  title: string
  subtitle?: string | null
  /** Label / value lines: patient, date, session or time, fee, booking ref. */
  lines: [string, string][]
  /** A bill's items: what, and what it came to. */
  items?: [string, string][]
  /** Totals under the items, the last one printed bold. */
  totals?: [string, string][]
  footer?: string | null
}

const KEY = 'clinic.m.printer'

export function isNativeApp(): boolean {
  return !!(window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.()
}

export function readPrinter(): PrinterPrefs {
  const fallback: PrinterPrefs = { method: isNativeApp() ? 'rawbt' : 'system', paper: 58, autoPrint: false }
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<PrinterPrefs> | null
    if (!raw) return fallback
    return {
      method: raw.method === 'rawbt' || raw.method === 'system' ? raw.method : fallback.method,
      paper: raw.paper === 80 ? 80 : 58,
      autoPrint: !!raw.autoPrint,
    }
  } catch {
    return fallback
  }
}

export function savePrinter(p: PrinterPrefs): void {
  try { localStorage.setItem(KEY, JSON.stringify(p)) } catch { /* private mode: defaults next time */ }
}

/** Plain ASCII for the printer's built-in font: the rupee sign and curly quotes become text. */
function ascii(s: string): string {
  return s
    .replace(/₹\s?/g, 'Rs.')
    .replace(/[‘’]/g, "'").replace(/[“”]/g, '"').replace(/[–—]/g, '-').replace(/·/g, '-')
    .normalize('NFKD').replace(/[^\x20-\x7E]/g, '')
}

/** Characters per line in the printer's standard font. */
const COLS: Record<PaperWidth, number> = { 58: 32, 80: 48 }

function wrap(text: string, width: number): string[] {
  const out: string[] = []
  for (const para of text.split('\n')) {
    let line = ''
    for (const word of para.split(/\s+/).filter(Boolean)) {
      if ((line ? line.length + 1 : 0) + word.length > width) {
        if (line) out.push(line)
        line = word.length > width ? word.slice(0, width) : word
      } else line = line ? `${line} ${word}` : word
    }
    out.push(line)
  }
  return out
}

/** "Label        value", the value pushed right; wrapped below when it will not fit. */
function pair(label: string, value: string, width: number): string[] {
  const room = width - label.length - 1
  if (value.length <= room) return [`${label}${' '.repeat(width - label.length - value.length)}${value}`]
  return [label, ...wrap(value, width).map((l) => ` ${l}`.slice(0, width))]
}

/** The slip as ESC/POS bytes: bold centred heading, the serial at four times size, then a cut. */
export function escpos(slip: TicketSlip, paper: PaperWidth): Uint8Array {
  const w = COLS[paper]
  const out: number[] = []
  const raw = (...b: number[]) => out.push(...b)
  const text = (s: string) => { for (const ch of ascii(s)) out.push(ch.charCodeAt(0)); out.push(0x0a) }
  const rule = () => text('-'.repeat(w))
  const INIT = [0x1b, 0x40]
  const CENTER = [0x1b, 0x61, 1]
  const LEFT = [0x1b, 0x61, 0]
  const BOLD = (on: boolean) => [0x1b, 0x45, on ? 1 : 0]
  const SIZE = (n: number) => [0x1d, 0x21, n]

  raw(...INIT, ...CENTER, ...BOLD(true), ...SIZE(0x01))
  for (const l of wrap(ascii(slip.clinicName), Math.floor(w))) text(l)
  raw(...SIZE(0), ...BOLD(false))
  if (slip.clinicAddress) for (const l of wrap(ascii(slip.clinicAddress), w)) text(l)
  if (slip.clinicPhone) text(`Ph: ${slip.clinicPhone}`)
  rule()
  raw(...BOLD(true)); text(slip.heading); raw(...BOLD(false))
  if (slip.serial != null) {
    raw(...SIZE(0x33)); text(`#${slip.serial}`); raw(...SIZE(0))
  }
  raw(...BOLD(true)); for (const l of wrap(ascii(slip.title), w)) text(l); raw(...BOLD(false))
  if (slip.subtitle) for (const l of wrap(ascii(slip.subtitle), w)) text(l)
  rule()
  raw(...LEFT)
  for (const [k, v] of slip.lines) for (const l of pair(ascii(k), ascii(v), w)) text(l)
  rule()
  if (slip.items?.length) {
    for (const [k, v] of slip.items) for (const l of pair(ascii(k), ascii(v), w)) text(l)
    rule()
  }
  if (slip.totals?.length) {
    slip.totals.forEach(([k, v], i) => {
      const last = i === slip.totals!.length - 1
      if (last) raw(...BOLD(true))
      for (const l of pair(ascii(k), ascii(v), w)) text(l)
      if (last) raw(...BOLD(false))
    })
    rule()
  }
  raw(...CENTER)
  if (slip.footer) for (const l of wrap(ascii(slip.footer), w)) text(l)
  text(`Printed ${new Date().toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`)
  raw(0x0a, 0x0a, 0x0a, 0x1d, 0x56, 0x42, 0x00) // feed and partial cut
  return Uint8Array.from(out)
}

function base64(bytes: Uint8Array): string {
  let s = ''
  for (const b of bytes) s += String.fromCharCode(b)
  return btoa(s)
}

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!)

/** The slip as a page sized to the roll, for the device's print dialog. */
export function slipHtml(slip: TicketSlip, paper: PaperWidth): string {
  const table = (rs: [string, string][], cls = '') => `<table class="${cls}">${rs.map(([k, v]) => `<tr><td>${esc(k)}</td><td>${esc(v)}</td></tr>`).join('')}</table><hr>`
  const rows = slip.lines.map(([k, v]) => `<tr><td>${esc(k)}</td><td>${esc(v)}</td></tr>`).join('')
  const extra = (slip.items?.length ? table(slip.items, 'items') : '') + (slip.totals?.length ? table(slip.totals, 'totals') : '')
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(slip.heading)}</title><style>
@page { size: ${paper}mm auto; margin: 0; }
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; background: #fff; color: #000; }
body { width: ${paper}mm; padding: 3mm ${paper === 58 ? 2.5 : 4}mm 6mm; font: 11px/1.35 ui-monospace, "SF Mono", Menlo, Consolas, monospace; }
.c { text-align: center; }
h1 { font-size: 15px; margin: 0 0 2px; font-weight: 800; }
.s { font-size: 10px; }
hr { border: 0; border-top: 1px dashed #000; margin: 6px 0; }
.h { font-weight: 800; letter-spacing: .08em; }
.n { font-size: 46px; font-weight: 900; line-height: 1.05; margin: 2px 0; }
.t { font-weight: 800; font-size: 13px; }
table { width: 100%; border-collapse: collapse; font: inherit; }
td { padding: 1px 0; vertical-align: top; }
td:last-child { text-align: right; font-weight: 700; padding-left: 6px; overflow-wrap: anywhere; }
table.items td:last-child, table.totals td:last-child { white-space: nowrap; }
table.items td:last-child { font-weight: 600; }
table.totals tr:last-child td { font-weight: 900; font-size: 13px; border-top: 1px solid #000; padding-top: 3px; }
</style></head><body>
<div class="c"><h1>${esc(slip.clinicName)}</h1>
${slip.clinicAddress ? `<div class="s">${esc(slip.clinicAddress)}</div>` : ''}
${slip.clinicPhone ? `<div class="s">Ph: ${esc(slip.clinicPhone)}</div>` : ''}
<hr><div class="h">${esc(slip.heading)}</div>
${slip.serial != null ? `<div class="n">#${slip.serial}</div>` : ''}
<div class="t">${esc(slip.title)}</div>
${slip.subtitle ? `<div class="s">${esc(slip.subtitle)}</div>` : ''}</div>
<hr><table>${rows}</table><hr>${extra}
<div class="c s">${slip.footer ? `${esc(slip.footer)}<br>` : ''}Printed ${esc(new Date().toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }))}</div>
</body></html>`
}

/**
 * Prints one slip the way this device is set up to. Resolves once it has been
 * handed over; the printer itself reports nothing back to a web page.
 */
export async function printSlip(slip: TicketSlip, prefs: PrinterPrefs = readPrinter()): Promise<void> {
  if (prefs.method === 'rawbt') {
    // RawBT registers the rawbt: scheme; the bytes ride in the address.
    window.location.href = `rawbt:base64,${base64(escpos(slip, prefs.paper))}`
    return
  }
  const frame = document.createElement('iframe')
  frame.setAttribute('aria-hidden', 'true')
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden'
  document.body.appendChild(frame)
  const doc = frame.contentDocument!
  doc.open(); doc.write(slipHtml(slip, prefs.paper)); doc.close()
  await new Promise((r) => setTimeout(r, 150))
  frame.contentWindow?.focus()
  frame.contentWindow?.print()
  // The dialog blocks on most browsers; the frame goes once it is closed.
  setTimeout(() => frame.remove(), 60_000)
}
